import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listWaitlist, markWaitlistNotified, removeWaitlist } from "@/lib/admin.functions";
import { adminAddWaitlist, convertWaitlistToReservation } from "@/lib/waitlist.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { BellRing, CalendarPlus, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/waitlist")({
  head: () => ({ meta: [
    { title: "Waitlist | Plunkets Admin" },
    { name: "description", content: "Manage Plunkets guest waitlist and confirm bookings when tables are available." },
    { property: "og:title", content: "Waitlist | Plunkets Admin" },
    { property: "og:description", content: "Plunkets guest waitlist and booking availability." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: WaitlistPage,
});

const BOOKING_TYPES = [
  { id: "pool_table", label: "Pool Table" },
  { id: "food_and_drinks", label: "Food & Drinks" },
  { id: "sports_viewing", label: "Sports Viewing" },
  { id: "darts", label: "Darts" },
  { id: "group_booking", label: "Group Booking" },
  { id: "karaoke", label: "Karaoke Terrace" },
] as const;

type BookingType = (typeof BOOKING_TYPES)[number]["id"];

function WaitlistPage() {
  const fn = useServerFn(listWaitlist);
  const notify = useServerFn(markWaitlistNotified);
  const rm = useServerFn(removeWaitlist);
  const convert = useServerFn(convertWaitlistToReservation);
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["waitlist"], queryFn: () => fn() });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["waitlist"] });
    qc.invalidateQueries({ queryKey: ["reservations"] });
    qc.invalidateQueries({ queryKey: ["table-reservations"] });
  };

  const mark = useMutation({
    mutationFn: (id: string) => notify({ data: { id } }),
    onSuccess: () => { toast.success("Marked as notified"); invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: (id: string) => rm({ data: { id } }),
    onSuccess: () => { toast.success("Removed"); invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const toBooking = useMutation({
    mutationFn: (id: string) => convert({ data: { id, duration_minutes: 90 } }),
    onSuccess: (r) => { toast.success(`Booking created · ${r.reference}`); invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl">Waitlist</h1>
          <p className="mt-1 text-sm text-muted-foreground">Guests waiting on availability.</p>
        </div>
        <AddWaitlistDialog onDone={invalidate} />
      </div>
      <div className="rounded-2xl border border-border bg-card/30">
        {isLoading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        ) : data.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">Waitlist is empty.</div>
        ) : (
          <ul className="divide-y divide-border">
            {data.map((w) => {
              const g = (w as { guests?: { full_name?: string; phone?: string; email?: string } }).guests;
              return (
                <li key={w.id} className="grid items-center gap-3 px-5 py-3 md:grid-cols-[1fr_160px_110px_170px_auto]">
                  <div>
                    <div className="text-sm font-semibold">{g?.full_name ?? "Guest"}</div>
                    <div className="text-xs text-muted-foreground">{g?.phone ?? "—"} · {g?.email ?? ""}</div>
                    {w.notes ? <div className="mt-1 text-xs text-muted-foreground">{w.notes}</div> : null}
                  </div>
                  <span className="text-xs uppercase tracking-wider text-primary">{w.booking_type.replace(/_/g, " ")}</span>
                  <span className="text-sm">party {w.party_size}</span>
                  <span className="text-xs text-muted-foreground">{new Date(w.desired_at).toLocaleString("en-IE", { timeZone: "Europe/Dublin" })}</span>
                  <div className="flex gap-1 justify-self-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={toBooking.isPending}
                      onClick={() => { if (confirm("Confirm this booking if tables are available?")) toBooking.mutate(w.id); }}
                    >
                      <CalendarPlus size={14} className="mr-1" /> Book
                    </Button>
                    <Button size="sm" variant="ghost" disabled={!!w.notified_at} onClick={() => mark.mutate(w.id)}>
                      <BellRing size={14} className="mr-1" /> {w.notified_at ? "Notified" : "Notify"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { if (confirm("Remove from waitlist?")) del.mutate(w.id); }}>
                      <Trash2 size={14} className="text-destructive" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function AddWaitlistDialog({ onDone }: { onDone: () => void }) {
  const add = useServerFn(adminAddWaitlist);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bookingType, setBookingType] = useState<BookingType>("food_and_drinks");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [party, setParty] = useState(2);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");

  async function submit() {
    setError(null);
    if (!date || !time || fullName.trim().length < 2 || phone.trim().length < 6) {
      setError("Name, phone, date and time are required.");
      return;
    }
    setBusy(true);
    try {
      await add({
        data: {
          booking_type: bookingType,
          desired_at: new Date(`${date}T${time}:00`).toISOString(),
          party_size: party,
          notes: notes || null,
          guest: {
            full_name: fullName,
            phone,
            email: email || null,
            marketing_consent: false,
          },
        },
      });
      toast.success("Added to waitlist");
      setOpen(false);
      setFullName(""); setPhone(""); setEmail(""); setNotes(""); setDate(""); setTime("");
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not add to waitlist");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm"><Plus size={14} className="mr-1" /> Add to waitlist</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add to waitlist</DialogTitle>
          <DialogDescription>Save a guest waiting on availability.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label>Booking type</Label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {BOOKING_TYPES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setBookingType(t.id)}
                  className={`rounded-md border px-2.5 py-1 text-xs transition ${
                    bookingType === t.id
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-primary/50"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="wl-date">Date</Label>
            <Input id="wl-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="wl-time">Time</Label>
            <Input id="wl-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="wl-party">Party size</Label>
            <Input
              id="wl-party"
              type="number"
              min={1}
              max={40}
              value={party}
              onChange={(e) => setParty(Number(e.target.value))}
            />
          </div>
          <div>
            <Label htmlFor="wl-phone">Phone</Label>
            <Input id="wl-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="wl-name">Full name</Label>
            <Input id="wl-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="wl-email">Email (optional)</Label>
            <Input id="wl-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="wl-notes">Notes</Label>
            <Textarea id="wl-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
